import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { useI18n } from '../i18n/I18nProvider'
import { Button, Card, FormActions, Grid, Stack, TextField, useToast } from '../components/ui'

/** « 30, 7, 1 » → [30, 7, 1] ; ce qui n'est pas un entier est ignoré, la base juge le reste. */
function lireRappels(saisie: string): number[] {
  return saisie
    .split(/[,;\s]+/)
    .map((m) => m.trim())
    .filter((m) => /^\d+$/.test(m))
    .map(Number)
}

/**
 * La politique du cycle de vie (migration 0077) : combien de jours un client en attente de
 * paiement garde la main avant la lecture seule, et à combien de jours de l'échéance il est
 * prévenu.
 *
 * LE BOUTON « EXÉCUTER MAINTENANT » N'EST PAS UN RACCOURCI DE TEST. La tâche tourne seule toutes
 * les six heures ; après avoir réactivé un client payé ou raccourci la grâce, le
 * super-administrateur veut voir l'effet sans attendre. Elle est idempotente : l'appuyer deux
 * fois ne fait rien de plus.
 */
export function BillingPolicyCard() {
  const { t, describeError } = useI18n()
  const { session } = useAuth()
  const { notify } = useToast()

  const token = session?.token ?? ''

  const [grace, setGrace] = useState('')
  const [rappels, setRappels] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [passage, setPassage] = useState(false)

  useEffect(() => {
    if (token === '') return

    const controle = new AbortController()

    api
      .getBillingPolicy(token, controle.signal)
      .then((p) => {
        setGrace(String(p.graceDays))
        setRappels(p.reminderDays.join(', '))
      })
      .catch((cause: unknown) => {
        if (!controle.signal.aborted) notify(describeError(cause), 'erreur')
      })

    return () => controle.abort()
  }, [token, notify, describeError])

  const enregistrer = async () => {
    setEnvoi(true)
    try {
      const p = await api.setBillingPolicy(token, { graceDays: Number(grace), reminderDays: lireRappels(rappels) })
      setRappels(p.reminderDays.join(', '))
      notify(t('platform.policy.saved'), 'succes')
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setEnvoi(false)
    }
  }

  const executer = async () => {
    setPassage(true)
    try {
      const bilan = await api.runPlatformLifecycle(token)
      notify(`${t('platform.policy.ran')} ${bilan.total}`, 'succes')
    } catch (cause: unknown) {
      notify(describeError(cause), 'erreur')
    } finally {
      setPassage(false)
    }
  }

  return (
    <Card title={t('platform.policy.title')}>
      <Stack gap={3}>
        <p className="m-0 text-[0.85rem] text-texte-doux">{t('platform.policy.description')}</p>
        <Grid min="15rem">
          <TextField
            label={t('platform.policy.graceDays')}
            value={grace}
            onChange={setGrace}
            type="number"
            min="0"
            max="90"
            hint={t('platform.policy.graceHint')}
            ltr
          />
          <TextField
            label={t('platform.policy.reminderDays')}
            value={rappels}
            onChange={setRappels}
            hint={t('platform.policy.reminderHint')}
            ltr
          />
        </Grid>
        <FormActions>
          <Button onClick={() => void enregistrer()} pending={envoi} pendingLabel={t('common.loading')}>
            {t('common.save')}
          </Button>
          <Button variant="secondaire" onClick={() => void executer()} pending={passage} pendingLabel={t('common.loading')}>
            {t('platform.policy.runNow')}
          </Button>
        </FormActions>
      </Stack>
    </Card>
  )
}
